/**
 * Findings Report panel — schema-driven form.
 *
 * On mount, GETs /api/ppt/findings/schema, then renders a form from whatever
 * the parser found in the template:
 *   - One field per {INPUT: key}, type chosen by the parser (text/date/
 *     dropdown/range/list)
 *   - Section checkboxes (default ON) for each {CLAUDE: include this section …}
 *   - Single-slide / single-bullet toggles
 *   - "+ Add" repeating list for {CLAUDE: insert each additional X …}
 *   - Per-Claude-generation file upload zone (max 3 files)
 *
 * Every field is OPTIONAL — empty fields become `[Humanized Key]` placeholders
 * in the output deck. Adding new {INPUT:} or {CLAUDE:} markers to the template
 * requires NO code change here.
 */

import React, { useEffect, useMemo, useState } from "react";
import type {
  FindingsForm,
  FindingsInputValue,
  FindingsTemplateSchema,
  InputMarker,
  InputType,
  MarkerLocation,
} from "../types";
import { getSavedContacts, saveContacts } from "../store/pptStore";
import { apiFetch } from "../../api/fddClient";

type Props = { projectId: string };

const PPTFindingsPanel: React.FC<Props> = ({ projectId }) => {
  const [schema, setSchema] = useState<FindingsTemplateSchema | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [inputs, setInputs] = useState<Record<string, FindingsInputValue>>({});
  const [sectionsIncluded, setSectionsIncluded] = useState<Record<string, boolean>>({});
  const [togglesIncluded, setTogglesIncluded] = useState<Record<string, boolean>>({});
  const [bulletListItems, setBulletListItems] = useState<Record<string, string[]>>({});
  const [uploads, setUploads] = useState<Record<string, { fileName: string; mimeType: string; base64: string }[]>>({});

  // Saved databook detected from the canonical project store. null = still
  // loading; { exists: false } = nothing saved yet for this project.
  const [savedDatabook, setSavedDatabook] = useState<
    { exists: boolean; sourceFilename?: string } | null
  >(null);

  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<{ fileName: string; slidesKept: number[]; slidesDeleted: number[] } | null>(null);

  // ── Load schema on mount ───────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch("/api/ppt/findings/schema");
        if (!res.ok) throw new Error(`Schema fetch failed (HTTP ${res.status})`);
        const data = (await res.json()) as FindingsTemplateSchema;
        if (cancelled) return;
        setSchema(data);

        const saved = getSavedContacts().values;
        const initialInputs: Record<string, FindingsInputValue> = {};
        for (const inp of data.inputs) {
          initialInputs[inp.key] = defaultValueFor(inp, saved[inp.key]);
        }
        setInputs(initialInputs);

        const initialSections: Record<string, boolean> = {};
        for (const s of data.sectionToggles) initialSections[s.sectionName] = true;
        setSectionsIncluded(initialSections);

        const initialToggles: Record<string, boolean> = {};
        for (const t of data.slideToggles) initialToggles[locationKey(t.location)] = true;
        for (const t of data.bulletToggles) initialToggles[locationKey(t.location)] = true;
        setTogglesIncluded(initialToggles);

        const initialBLists: Record<string, string[]> = {};
        for (const bl of data.bulletLists) initialBLists[locationKey(bl.location)] = [];
        setBulletListItems(initialBLists);

        const initialUploads: Record<string, typeof uploads[string]> = {};
        for (const cg of data.claudeGenerations) initialUploads[locationKey(cg.location)] = [];
        setUploads(initialUploads);
      } catch (e: any) {
        if (!cancelled) setLoadError(e?.message || "Could not load template schema");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Group inputs visually: contacts together, everything else flat ─
  const { contactGroups, regularInputs } = useMemo(() => {
    if (!schema) return { contactGroups: {} as Record<number, InputMarker[]>, regularInputs: [] as InputMarker[] };
    const contacts: Record<number, InputMarker[]> = {};
    const regular: InputMarker[] = [];
    for (const inp of schema.inputs) {
      const m = /^contact_(\d+)_/.exec(inp.key);
      if (m) {
        const n = parseInt(m[1], 10);
        if (!contacts[n]) contacts[n] = [];
        contacts[n].push(inp);
      } else {
        regular.push(inp);
      }
    }
    return { contactGroups: contacts, regularInputs: regular };
  }, [schema]);

  // ── Update helpers ────────────────────────────────────────────────
  const setInputValue = (key: string, value: FindingsInputValue) =>
    setInputs((prev) => ({ ...prev, [key]: value }));

  const updateRangeStart = (key: string, start: string) =>
    setInputs((prev) => {
      const cur = prev[key];
      const range = isRangeValue(cur) ? cur : { start: "", end: "" };
      return { ...prev, [key]: { ...range, start } };
    });

  const updateRangeEnd = (key: string, end: string) =>
    setInputs((prev) => {
      const cur = prev[key];
      const range = isRangeValue(cur) ? cur : { start: "", end: "" };
      return { ...prev, [key]: { ...range, end } };
    });

  const updateListItem = (key: string, idx: number, value: string) =>
    setInputs((prev) => {
      const cur = prev[key];
      const list = Array.isArray(cur) ? [...cur] : [];
      list[idx] = value;
      return { ...prev, [key]: list };
    });

  const addListItem = (key: string) =>
    setInputs((prev) => {
      const cur = prev[key];
      const list = Array.isArray(cur) ? [...cur, ""] : [""];
      return { ...prev, [key]: list };
    });

  const removeListItem = (key: string, idx: number) =>
    setInputs((prev) => {
      const cur = prev[key];
      const list = Array.isArray(cur) ? cur.filter((_, i) => i !== idx) : [];
      return { ...prev, [key]: list };
    });

  // ── Detect a saved databook for this project ──────────────────────
  useEffect(() => {
    if (!projectId) {
      setSavedDatabook({ exists: false });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`/api/databook-store/${encodeURIComponent(projectId)}/status`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as { exists: boolean; sourceFilename?: string };
        if (!cancelled) setSavedDatabook({ exists: Boolean(data.exists), sourceFilename: data.sourceFilename });
      } catch {
        if (!cancelled) setSavedDatabook({ exists: false });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // ── Generate ──────────────────────────────────────────────────────
  const handleGenerate = async () => {
    setGenError(null);
    setGenerating(true);
    setLastResult(null);

    // Persist any contact values the analyst just typed.
    const flatContactStrings: Record<string, string> = {};
    for (const [k, v] of Object.entries(inputs)) {
      if (typeof v === "string") flatContactStrings[k] = v;
    }
    saveContacts(flatContactStrings);

    const payload: FindingsForm = {
      inputs: { ...inputs },
      sectionsIncluded: { ...sectionsIncluded },
      togglesIncluded: { ...togglesIncluded },
      bulletListItems: { ...bulletListItems },
      uploads: { ...uploads },
    };

    try {
      const res = await apiFetch("/api/ppt/findings/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.message || err?.error || `Generation failed (HTTP ${res.status})`);
      }
      const data = (await res.json()) as {
        fileName: string;
        pptxBase64: string;
        slidesKept: number[];
        slidesDeleted: number[];
      };
      triggerPptxDownload(data.pptxBase64, data.fileName);
      setLastResult({ fileName: data.fileName, slidesKept: data.slidesKept, slidesDeleted: data.slidesDeleted });
    } catch (e: any) {
      setGenError(e?.message || "Could not generate the deck.");
    } finally {
      setGenerating(false);
    }
  };

  if (loadError) return <div style={errorStyle}>Failed to load template schema: {loadError}</div>;
  if (!schema) return <div style={{ color: "#6b7280", fontSize: 13 }}>Loading template…</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div>
        <h3 style={{ margin: 0, color: "var(--theme-ink)", fontSize: 18, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 22 }}>🎯</span>
          Storyboard
        </h3>
        <div style={{ fontSize: 13, color: "var(--theme-ink-soft)", marginTop: 4 }}>
          Fill in whatever you like — anything blank ships as <code style={inlineCode}>[Field Name]</code> placeholder
          text in the deck. Adding new <code style={inlineCode}>{`{INPUT:}`}</code> /{" "}
          <code style={inlineCode}>{`{CLAUDE:}`}</code> markers to the template makes them appear here automatically.
        </div>
        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 6 }}>
          Template: <strong>{schema.templateId}</strong> · {schema.templateVersion} · {schema.slideCount} slides ·{" "}
          {schema.inputs.length} inputs · {schema.sectionToggles.length} section toggles ·{" "}
          {schema.claudeGenerations.length} Claude generation step{schema.claudeGenerations.length === 1 ? "" : "s"}
        </div>
      </div>

      {/* ─────── Databook tables (detected from project store) ─────── */}
      <Section
        title="Databook tables"
        subtitle="Reads from this project's saved databook in the canonical store."
      >
        {savedDatabook === null && (
          <div style={{ fontSize: 12, color: "#6b7280" }}>Checking for a saved databook…</div>
        )}
        {savedDatabook && savedDatabook.exists && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 12,
              background: "var(--theme-tint-7)",
              border: "1px solid var(--theme-border)",
              borderRadius: 6,
              padding: "8px 10px",
            }}
          >
            <span>📊</span>
            <span>
              Databook detected: <strong>{savedDatabook.sourceFilename || "saved databook"}</strong>. To use a
              different one, re-upload via the <strong>FDD Toolbox → Databook</strong> tool.
            </span>
          </div>
        )}
        {savedDatabook && !savedDatabook.exists && (
          <div style={{ fontSize: 12, color: "#6b7280" }}>
            No databook saved for this project yet. Build or upload one from the{" "}
            <strong>FDD Toolbox → Databook</strong> tool first.
          </div>
        )}
        <div
          style={{
            fontSize: 11,
            color: "#92400e",
            background: "#fffbeb",
            border: "1px solid #fde68a",
            borderRadius: 6,
            padding: "6px 8px",
          }}
        >
          🚧 Table embedding is still in progress — stay tuned.
        </div>
      </Section>

      {/* ─────── Inputs (regular) ─────── */}
      {regularInputs.length > 0 && (
        <Section title="Project metadata">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {regularInputs.map((inp) => (
              <Field key={inp.key} label={fieldLabel(inp)}>
                <TypedInput
                  inp={inp}
                  value={inputs[inp.key]}
                  onChange={(v) => setInputValue(inp.key, v)}
                  onListAdd={() => addListItem(inp.key)}
                  onListRemove={(i) => removeListItem(inp.key, i)}
                  onListItemChange={(i, v) => updateListItem(inp.key, i, v)}
                  onRangeStart={(v) => updateRangeStart(inp.key, v)}
                  onRangeEnd={(v) => updateRangeEnd(inp.key, v)}
                />
              </Field>
            ))}
          </div>
        </Section>
      )}

      {/* ─────── Inputs (contacts grouped, all optional) ─────── */}
      {Object.keys(contactGroups).length > 0 && (
        <Section title="Leo Berwick contacts" subtitle="All optional. Saved across deals so they auto-fill next time.">
          {Object.keys(contactGroups)
            .map(Number)
            .sort((a, b) => a - b)
            .map((n) => (
              <div key={n} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--theme-ink-soft)", marginBottom: 6 }}>
                  Contact {n}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                  {contactGroups[n]
                    .sort((a, b) => a.key.localeCompare(b.key))
                    .map((inp) => (
                      <Field key={inp.key} label={humanize(inp.key.replace(/^contact_\d+_/, ""))}>
                        <TypedInput
                          inp={inp}
                          value={inputs[inp.key]}
                          onChange={(v) => setInputValue(inp.key, v)}
                          onListAdd={() => addListItem(inp.key)}
                          onListRemove={(i) => removeListItem(inp.key, i)}
                          onListItemChange={(i, v) => updateListItem(inp.key, i, v)}
                          onRangeStart={(v) => updateRangeStart(inp.key, v)}
                          onRangeEnd={(v) => updateRangeEnd(inp.key, v)}
                        />
                      </Field>
                    ))}
                </div>
              </div>
            ))}
        </Section>
      )}

      {/* ─────── Sections to include ─────── */}
      {schema.sectionToggles.length > 0 && (
        <Section title="Sections to include" subtitle="Unchecked sections are deleted from the output deck.">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {schema.sectionToggles.map((s) => (
              <label key={s.sectionName} style={checkboxLabelStyle}>
                <input
                  type="checkbox"
                  checked={sectionsIncluded[s.sectionName] ?? true}
                  onChange={(e) => setSectionsIncluded((prev) => ({ ...prev, [s.sectionName]: e.target.checked }))}
                />
                <span style={{ marginLeft: 6 }}>
                  {s.sectionName}
                  <span style={{ fontSize: 10, color: "#6b7280", marginLeft: 6 }}>
                    (slides {s.slideIndices[0]}–{s.slideIndices[s.slideIndices.length - 1]})
                  </span>
                </span>
              </label>
            ))}
          </div>
        </Section>
      )}

      {/* ─────── Slide / bullet toggles ─────── */}
      {(schema.slideToggles.length + schema.bulletToggles.length) > 0 && (
        <Section title="Conditional content">
          {schema.slideToggles.map((t) => {
            const k = locationKey(t.location);
            return (
              <label key={`s-${k}`} style={checkboxLabelStyle}>
                <input
                  type="checkbox"
                  checked={togglesIncluded[k] ?? true}
                  onChange={(e) => setTogglesIncluded((prev) => ({ ...prev, [k]: e.target.checked }))}
                />
                <span style={{ marginLeft: 6, fontSize: 12 }}>
                  {sentenceCase(
                    t.conditionPrompt
                      .replace(/^include this slide only if/i, "")
                      .replace(/\.\s*Otherwise.*$/i, "")
                      .trim() || "Include conditional slide",
                  )}
                  <span style={{ fontSize: 10, color: "#6b7280", marginLeft: 6 }}>
                    (slide {t.location.slideIndex})
                  </span>
                </span>
              </label>
            );
          })}
          {schema.bulletToggles.map((t) => {
            const k = locationKey(t.location);
            return (
              <label key={`b-${k}`} style={checkboxLabelStyle}>
                <input
                  type="checkbox"
                  checked={togglesIncluded[k] ?? true}
                  onChange={(e) => setTogglesIncluded((prev) => ({ ...prev, [k]: e.target.checked }))}
                />
                <span style={{ marginLeft: 6, fontSize: 12 }}>
                  {sentenceCase(t.conditionPrompt.replace(/^include this bullet only if/i, "").trim() || "Include conditional bullet")}
                  <span style={{ fontSize: 10, color: "#6b7280", marginLeft: 6 }}>(slide {t.location.slideIndex})</span>
                </span>
              </label>
            );
          })}
        </Section>
      )}

      {/* ─────── Supporting documents (bucketed, coming soon) ─────── */}
      {schema.claudeGenerations.length > 0 && (
        <Section
          title="Supporting documents for AI-generated slides"
          subtitle={`Supporting files will ground Claude's slide-by-slide generation (e.g. board decks, AFS, mgmt-meeting notes). The template currently has ${schema.claudeGenerations.length} slot${schema.claudeGenerations.length === 1 ? "" : "s"} that will accept uploads.`}
        >
          <div
            style={{
              fontSize: 11,
              color: "#92400e",
              background: "#fffbeb",
              border: "1px solid #fde68a",
              borderRadius: 6,
              padding: "6px 8px",
            }}
          >
            🚧 Coming soon — stay tuned.
          </div>
        </Section>
      )}

      {/* ─────── Submit ─────── */}
      {genError && <div style={errorStyle}>{genError}</div>}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button
          onClick={() => void handleGenerate()}
          disabled={generating}
          style={{
            background: generating ? "var(--theme-action-disabled)" : "var(--theme-action)",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "10px 16px",
            cursor: generating ? "not-allowed" : "pointer",
            fontWeight: 700,
            fontSize: 13,
          }}
        >
          {generating ? "Generating…" : "📑 Generate findings report"}
        </button>
      </div>

      {lastResult && (
        <div
          style={{
            background: "#ecfdf5",
            border: "1px solid #6ee7b7",
            borderRadius: 8,
            padding: "10px 12px",
            fontSize: 12,
            color: "#065f46",
          }}
        >
          ✓ Downloaded <strong>{lastResult.fileName}</strong> · {lastResult.slidesKept.length} slides kept ·{" "}
          {lastResult.slidesDeleted.length} slides removed.
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────── TypedInput ─────────────────────────── */

type TypedInputProps = {
  inp: InputMarker;
  value: FindingsInputValue | undefined;
  onChange: (v: FindingsInputValue) => void;
  onListAdd: () => void;
  onListRemove: (idx: number) => void;
  onListItemChange: (idx: number, v: string) => void;
  onRangeStart: (v: string) => void;
  onRangeEnd: (v: string) => void;
};

const TypedInput: React.FC<TypedInputProps> = ({ inp, value, onChange, onListAdd, onListRemove, onListItemChange, onRangeStart, onRangeEnd }) => {
  switch (inp.inputType) {
    case "date":
      return (
        <input
          type="date"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        />
      );

    case "dropdown":
      return (
        <select
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        >
          <option value="">— Select —</option>
          {(inp.options ?? []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );

    case "range": {
      const range = isRangeValue(value) ? value : { start: "", end: "" };
      return (
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input
            type="text"
            placeholder="Start (e.g. Jan-23)"
            value={range.start}
            onChange={(e) => onRangeStart(e.target.value)}
            style={{ ...inputStyle, flex: 1 }}
          />
          <span style={{ fontSize: 11, color: "#6b7280" }}>to</span>
          <input
            type="text"
            placeholder="End (e.g. Mar-25)"
            value={range.end}
            onChange={(e) => onRangeEnd(e.target.value)}
            style={{ ...inputStyle, flex: 1 }}
          />
        </div>
      );
    }

    case "list": {
      const list = Array.isArray(value) ? value : [];
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {list.map((v, idx) => (
            <div key={idx} style={{ display: "flex", gap: 4 }}>
              <input
                type="text"
                value={v}
                placeholder="e.g. FY24"
                onChange={(e) => onListItemChange(idx, e.target.value)}
                style={{ ...inputStyle, flex: 1 }}
              />
              <button onClick={() => onListRemove(idx)} style={removeBtnStyle} title="Remove">✕</button>
            </div>
          ))}
          <button onClick={onListAdd} style={{ ...addBtnStyle, alignSelf: "flex-start" }}>+ Add</button>
        </div>
      );
    }

    case "text":
    default:
      return (
        <input
          type="text"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        />
      );
  }
};

/* ─────────────────────────── Helpers ─────────────────────────── */

function locationKey(loc: MarkerLocation): string {
  return `${loc.slideIndex}:${loc.paragraphIndex}`;
}

function isRangeValue(v: FindingsInputValue | undefined): v is { start: string; end: string } {
  return typeof v === "object" && v !== null && !Array.isArray(v) && "start" in v && "end" in v;
}

function defaultValueFor(inp: InputMarker, savedString?: string): FindingsInputValue {
  if (inp.inputType === "list") return [];
  if (inp.inputType === "range") return { start: "", end: "" };
  return savedString ?? "";
}

function fieldLabel(inp: InputMarker): string {
  // free-form prompt keys (with spaces) render as-is.
  if (/\s/.test(inp.key)) return inp.key;
  return humanize(inp.key);
}

function humanize(key: string): string {
  if (key.includes(" ")) return key;
  return key
    .split("_")
    .map((w) => (w.length > 0 ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

function sentenceCase(s: string): string {
  if (!s) return s;
  return s[0].toUpperCase() + s.slice(1);
}

function triggerPptxDownload(base64: string, fileName: string) {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const blob = new Blob([bytes], { type: "application/vnd.openxmlformats-officedocument.presentationml.presentation" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* ─────────────────────────── Style helpers ─────────────────────────── */

const inputStyle: React.CSSProperties = {
  padding: "8px 10px",
  borderRadius: 6,
  border: "1px solid var(--theme-border)",
  fontSize: 13,
  background: "#fff",
  width: "100%",
  boxSizing: "border-box",
};

const errorStyle: React.CSSProperties = {
  background: "#fef2f2",
  border: "1px solid #fca5a5",
  borderRadius: 8,
  padding: "8px 12px",
  fontSize: 12,
  color: "#991b1b",
};

const inlineCode: React.CSSProperties = {
  fontFamily: "monospace",
  fontSize: 11,
  background: "var(--theme-tint-15)",
  padding: "1px 4px",
  borderRadius: 3,
  color: "var(--theme-action)",
};

const checkboxLabelStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  background: "var(--theme-tint-7)",
  border: "1px solid var(--theme-border)",
  borderRadius: 6,
  padding: "6px 10px",
  fontSize: 12,
  color: "#1f2937",
  cursor: "pointer",
};

const removeBtnStyle: React.CSSProperties = {
  background: "transparent",
  color: "#dc2626",
  border: "1px solid #fca5a5",
  borderRadius: 4,
  padding: "2px 6px",
  fontSize: 11,
  cursor: "pointer",
};

const addBtnStyle: React.CSSProperties = {
  background: "#fff",
  color: "var(--theme-ink)",
  border: "1px solid var(--theme-border)",
  borderRadius: 6,
  padding: "6px 10px",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
    <span style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>{label}</span>
    {children}
  </label>
);

const Section: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({ title, subtitle, children }) => (
  <div
    style={{
      background: "#ffffff",
      border: "1px solid var(--theme-border)",
      borderRadius: 8,
      padding: 12,
      display: "flex",
      flexDirection: "column",
      gap: 10,
    }}
  >
    <div>
      <div style={{ fontWeight: 700, color: "var(--theme-ink)", fontSize: 13 }}>{title}</div>
      {subtitle && <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{subtitle}</div>}
    </div>
    {children}
  </div>
);

export default PPTFindingsPanel;
